import { useId, useState, type ReactNode } from "react";

/**
 * AÇILIR BÖLÜM — uzun sayfaları başlık satırlarına indirger.
 *
 * Başlık satırı her zaman görünür ve bölümün ÖZETİNİ taşır (`meta`: "148 ürün
 * · ₺1,2M" gibi); ayrıntı yalnızca açılınca çizilir. Açık/kapalı durumu
 * `storageKey` verilirse tarayıcıda hatırlanır — kullanıcı her girişte aynı
 * bölümleri yeniden açmak zorunda kalmaz.
 *
 * Kapalı bölümün içeriği DOM'a hiç konmaz: büyük tablolar kapalıyken sayfayı
 * yavaşlatmaz.
 */
export function Collapsible({
  title,
  meta,
  actions,
  defaultOpen = false,
  storageKey,
  children,
  className = "",
}: {
  title: ReactNode;
  /** Başlığın altında küçük özet (sayı, toplam). */
  meta?: ReactNode;
  /** Sağdaki düğmeler (Excel'e indir vb.) — tıklamaları bölümü açıp kapatmaz. */
  actions?: ReactNode;
  defaultOpen?: boolean;
  storageKey?: string;
  children: ReactNode;
  className?: string;
}) {
  const id = useId();
  const [open, setOpen] = useState<boolean>(() => {
    if (!storageKey) return defaultOpen;
    try {
      const v = localStorage.getItem(`balder.acik.${storageKey}`);
      return v === null ? defaultOpen : v === "1";
    } catch {
      return defaultOpen;
    }
  });

  const toggle = () => {
    setOpen((o) => {
      const n = !o;
      if (storageKey) {
        try {
          localStorage.setItem(`balder.acik.${storageKey}`, n ? "1" : "0");
        } catch {
          // Depolama kapalıysa yalnızca bu oturumda geçerli.
        }
      }
      return n;
    });
  };

  return (
    <section className={`card overflow-hidden ${className}`}>
      <div
        className={`flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 sm:px-5 ${
          open ? "border-b border-gray-100" : ""
        }`}
      >
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-controls={id}
          className="group -my-1 flex min-h-[44px] min-w-0 flex-1 items-center gap-3 rounded-lg text-left lg:min-h-[36px]"
        >
          <span
            aria-hidden="true"
            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-500 transition-transform duration-200 group-hover:bg-gray-200 ${
              open ? "rotate-90" : ""
            }`}
          >
            <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                clipRule="evenodd"
              />
            </svg>
          </span>
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-gray-900">{title}</span>
            {meta ? <span className="mt-0.5 block text-xs text-gray-500">{meta}</span> : null}
          </span>
        </button>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
      {open ? (
        <div id={id} className="animate-fade-in p-4 sm:p-5">
          {children}
        </div>
      ) : null}
    </section>
  );
}
